import { useState } from 'react';
import { Trash2, X } from 'lucide-react';
import { useDungeonEditorStore } from '@/store/dungeonEditorStore';
import type { DungeonTrigger } from '@/lib/db/dungeon-types';

const labelClass = 'block text-xs text-gray-500 uppercase tracking-wide mb-1';
const inputClass = 'w-full px-2 py-1.5 bg-gray-950 border border-gray-700 rounded text-sm text-gray-100';

function getEffectData(trigger: DungeonTrigger): Record<string, unknown> {
  return trigger.effectData ?? {};
}

export function TriggerPanel() {
  const selectedTriggerId = useDungeonEditorStore((s) => s.selectedTriggerId);
  const mapData = useDungeonEditorStore((s) => s.mapData);
  const updateTrigger = useDungeonEditorStore((s) => s.updateTrigger);
  const removeTrigger = useDungeonEditorStore((s) => s.removeTrigger);

  const [flagInput, setFlagInput] = useState('');

  const trigger = selectedTriggerId
    ? mapData.triggers.find((t) => t.id === selectedTriggerId) ?? null
    : null;

  if (!trigger) {
    return (
      <div className="text-xs text-gray-500 italic">No trigger selected.</div>
    );
  }

  const effectData = getEffectData(trigger);

  function update(updates: Partial<DungeonTrigger>) {
    updateTrigger(trigger!.id, updates);
  }

  function updateEffectData(key: string, value: unknown) {
    update({ effectData: { ...effectData, [key]: value } });
  }

  function handleAddFlag(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      const flag = flagInput.trim();
      if (flag && !trigger!.requiredFlags.includes(flag)) {
        update({ requiredFlags: [...trigger!.requiredFlags, flag] });
      }
      setFlagInput('');
    }
  }

  function removeFlag(flag: string) {
    update({ requiredFlags: trigger!.requiredFlags.filter((f) => f !== flag) });
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-200">Trigger</h2>
        <button
          onClick={() => removeTrigger(trigger.id)}
          className="p-1 text-gray-500 hover:text-red-400 transition-colors"
          title="Delete trigger"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Name */}
      <div>
        <label className={labelClass}>Name</label>
        <input
          type="text"
          className={inputClass}
          value={trigger.name}
          onChange={(e) => update({ name: e.target.value })}
        />
      </div>

      {/* Condition + Effect side by side */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={labelClass}>Condition</label>
          <select
            className={inputClass}
            value={trigger.condition}
            onChange={(e) =>
              update({ condition: e.target.value as DungeonTrigger['condition'] })
            }
          >
            <option value="step">Step</option>
            <option value="proximity">Proximity</option>
            <option value="visible">Visible</option>
            <option value="interact">Interact</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Effect</label>
          <select
            className={inputClass}
            value={trigger.effect}
            onChange={(e) =>
              update({ effect: e.target.value as DungeonTrigger['effect'], effectData: {} })
            }
          >
            <option value="dialogue">Dialogue</option>
            <option value="combat">Combat</option>
            <option value="trap">Trap</option>
            <option value="teleport">Teleport</option>
            <option value="spawn">Spawn</option>
            <option value="custom">Custom</option>
          </select>
        </div>
      </div>

      {/* One-shot */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={trigger.oneShot}
          onChange={(e) => update({ oneShot: e.target.checked })}
          className="accent-violet-500"
        />
        <span className="text-xs text-gray-400">One-shot (fires once)</span>
      </label>

      {/* Effect-specific fields */}
      {trigger.effect === 'dialogue' && (
        <div className="space-y-3 border-t border-gray-800 pt-3">
          <div>
            <label className={labelClass}>Text</label>
            <textarea
              className={`${inputClass} resize-none`}
              rows={3}
              value={typeof effectData.text === 'string' ? effectData.text : ''}
              onChange={(e) => updateEffectData('text', e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Speaker (optional)</label>
            <input
              type="text"
              className={inputClass}
              value={typeof effectData.speaker === 'string' ? effectData.speaker : ''}
              onChange={(e) => updateEffectData('speaker', e.target.value)}
            />
          </div>
        </div>
      )}

      {trigger.effect === 'trap' && (
        <div className="space-y-3 border-t border-gray-800 pt-3">
          <div>
            <label className={labelClass}>Damage</label>
            <input
              type="number"
              className={inputClass}
              value={typeof effectData.damage === 'number' ? effectData.damage : 0}
              min={0}
              onChange={(e) => updateEffectData('damage', Number(e.target.value))}
            />
          </div>
          <div>
            <label className={labelClass}>Description</label>
            <textarea
              className={`${inputClass} resize-none`}
              rows={2}
              value={typeof effectData.description === 'string' ? effectData.description : ''}
              onChange={(e) => updateEffectData('description', e.target.value)}
            />
          </div>
        </div>
      )}

      {trigger.effect === 'combat' && (
        <div className="space-y-3 border-t border-gray-800 pt-3">
          <div>
            <label className={labelClass}>Encounter ID</label>
            <input
              type="text"
              className={inputClass}
              value={typeof effectData.encounterId === 'string' ? effectData.encounterId : ''}
              onChange={(e) => updateEffectData('encounterId', e.target.value)}
            />
          </div>
        </div>
      )}

      {trigger.effect === 'teleport' && (
        <div className="border-t border-gray-800 pt-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelClass}>Target X</label>
              <input
                type="number"
                className={inputClass}
                value={typeof effectData.targetX === 'number' ? effectData.targetX : 0}
                min={0}
                onChange={(e) => updateEffectData('targetX', Number(e.target.value))}
              />
            </div>
            <div>
              <label className={labelClass}>Target Y</label>
              <input
                type="number"
                className={inputClass}
                value={typeof effectData.targetY === 'number' ? effectData.targetY : 0}
                min={0}
                onChange={(e) => updateEffectData('targetY', Number(e.target.value))}
              />
            </div>
          </div>
        </div>
      )}

      {trigger.effect === 'custom' && (
        <div className="space-y-3 border-t border-gray-800 pt-3">
          <div>
            <label className={labelClass}>Event Name</label>
            <input
              type="text"
              className={inputClass}
              placeholder="ward_broken"
              value={typeof effectData.eventName === 'string' ? effectData.eventName : ''}
              onChange={(e) => updateEffectData('eventName', e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Flags section */}
      <div className="border-t border-gray-800 pt-3 space-y-3">
        <div>
          <label className={labelClass}>Required Flags</label>
          {trigger.requiredFlags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {trigger.requiredFlags.map((flag) => (
                <span
                  key={flag}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-800 border border-gray-700 rounded text-xs text-gray-300"
                >
                  {flag}
                  <button
                    onClick={() => removeFlag(flag)}
                    className="text-gray-500 hover:text-gray-200 transition-colors"
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <input
            type="text"
            className={inputClass}
            placeholder="Add flag + Enter"
            value={flagInput}
            onChange={(e) => setFlagInput(e.target.value)}
            onKeyDown={handleAddFlag}
          />
        </div>

        <div>
          <label className={labelClass}>Sets Flag</label>
          <input
            type="text"
            className={inputClass}
            placeholder="rune_1"
            value={trigger.setsFlag ?? ''}
            onChange={(e) =>
              update({ setsFlag: e.target.value || undefined })
            }
          />
        </div>
      </div>

      {/* Position info */}
      <div className="border-t border-gray-800 pt-3">
        <label className={labelClass}>Position</label>
        <div className="bg-gray-950 border border-gray-700 rounded px-2 py-1.5 space-y-1">
          {trigger.targetX !== undefined && trigger.targetY !== undefined ? (
            <div className="text-xs text-gray-400">
              Cell: <span className="text-gray-200">{trigger.targetX}, {trigger.targetY}</span>
            </div>
          ) : null}
          {trigger.targetEntityId ? (
            <div className="text-xs text-gray-400">
              Entity: <span className="text-gray-200 font-mono">{trigger.targetEntityId}</span>
            </div>
          ) : null}
          {trigger.roomId ? (
            <div className="text-xs text-gray-400">
              Room: <span className="text-gray-200 font-mono">{trigger.roomId}</span>
            </div>
          ) : null}
          {trigger.targetX === undefined && trigger.targetY === undefined && !trigger.targetEntityId && !trigger.roomId && (
            <div className="text-xs text-gray-500 italic">No position set</div>
          )}
        </div>
      </div>
    </div>
  );
}
